import React, { useRef } from 'react';
import styled, { withTheme } from 'styled-components';
import { ModalBody, ModalHeader } from 'reactstrap';
import 'react-image-gallery/styles/css/image-gallery.css';
import ImageGallery from 'react-image-gallery';
import { WindowWidthContext } from '@minecraft.utils';
import { getEnvironment } from '@minecraft.environment';
import { Theme } from '@minecraft.themes';
import Icon from '@minecraft.icon';
import { gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import CloudinaryPhoto, { CloudinaryPhotoProps } from '@minecraft.cloudinary-photo';
import CloudinaryVideoAudio, { CloudinaryVideoAudioProps } from '@minecraft.cloudinary-video-audio';
import PreventDownloadContainer from '@minecraft.prevent-download-container';
import { useTranslation, IntlDateFormatName } from '@blocs.i18n';
import SCREEN_NAMES from './screenNames';
import {
  StyledModal,
  StyledPrevButton,
  StyledNextButton,
  StyledCloseButton,
  ThumbNav,
  ThumbNextButton,
  ThumbPrevButton,
} from './styles';

const env = getEnvironment();

const StyledModalBody = styled(ModalBody)`
  flex: 1;
`;

const StyledLegend = styled.div`
  color: ${(props): string => props.theme.colors.primary.white};
  background: ${(props): string => props.theme.colors.primary.black};
  padding: 0.0875rem;
  margin: 0 auto;
  line-height: normal;
  text-align: center;
`;

const AUDIO_THUMBNAIL = `${env.CDN_URL}/images/icons/b21/audio-thumbnail.svg`;

const DOCUMENT_THUMBNAIL = `${env.CDN_URL}/images/icons/b21/document-thumbnail.svg`;

type MediaViewerMediaObject = {
  media: {
    fileKey?: string;
    guid?: string; // guid is deprecated but here for legacy uses
    mediaType?: { code: string };
    mediaStatus?: { code: string };
    url?: string;
    mediaStorageStatus?: { code: string };
    created?: string; // iso date string
    isLocked?: boolean;
    transformation?: CloudinaryPhotoProps['transformation'];
    thumbnailUrl?: string;
    thumbnail?: CloudinaryVideoAudioProps['thumbnail'];
    name?: string;
  };
};

type Props = {
  mediaList: MediaViewerMediaObject[];
  stageName?: string;
  startIndex?: number;
  handleCloseModal?: () => void;
  theme: Theme;
  isSmall?: boolean;
  withDownloadLink?: boolean;
  preventDownload?: boolean;
  zIndex?: number | string;
  hideLockedBanner?: boolean;
  withLegend?: boolean;
  legendFormat?: IntlDateFormatName;
};

const heightDeltas = 95; //  = Header height + paddings & footer paddings
const mobileHeightDeltas = 85; // = Header height + paddings & footer paddings for mobile view
const defaultHeight = 1024 - heightDeltas; // / = 1024(default) - header&footer
const defaultMobileHeight = 667 - mobileHeightDeltas; // / = 667(default for mobiles) - header&footer
const baseThumbnailheight = 81; // Per Zeplin Design https://app.zeplin.io/project/5c3f7fde99f9c618725999a7/screen/5e861b196b2789229cb6beb8
const baseThumbnailWidth = 144; // Per Zeplin Design https://app.zeplin.io/project/5c3f7fde99f9c618725999a7/screen/5e861b196b2789229cb6beb8

/** *
 * isMobile - true if mobile
 * result is {
 *   mainHeight : inner area height,
 *   mainWidth : inner area width,
 *   thumbSize : thumbnail height,
 * }
 * */
const calcBaseDimension = (isMobile: boolean) => {
  const baseHeight = isMobile ? defaultMobileHeight : defaultHeight; // select default height
  const baseImageHeight = isMobile ? 454 : 720; // select default image height
  const deltas = isMobile ? mobileHeightDeltas : heightDeltas; // select deltas for calculation

  const thumbHeight = Math.floor(((window.innerHeight - deltas) * baseThumbnailheight) / defaultHeight); // calculate thumbnail height
  const mainHeight = Math.floor(((window.innerHeight - deltas) * baseImageHeight) / baseHeight); // calculate inner area height based on viewport
  const mainWidth = Math.min(window.innerWidth - 12, mainHeight); // calculate inner area width based on viewport

  return {
    mainHeight: isMobile ? Math.min(503, mainHeight) : mainHeight,
    mainWidth,
    thumbHeight,
    thumbWidth: baseThumbnailWidth,
  };
};

const CloudinaryMediaViewer = ({
  handleCloseModal,
  hideLockedBanner = false,
  isSmall = false,
  legendFormat = 'MONTH_DAY_YEAR_TIME',
  mediaList,
  preventDownload,
  stageName,
  startIndex = 0,
  theme,
  withDownloadLink = true,
  withLegend = false,
  zIndex = '',
}: Props): JSX.Element => {
  const { formatIntlDate, t } = useTranslation();
  const slider = useRef(null);
  const widthData = WindowWidthContext.useWidthData();
  const { mainWidth, mainHeight, thumbHeight, thumbWidth } = calcBaseDimension(widthData.lessMd);
  const mainDimensions = [mainWidth, mainHeight] as [number, number];
  const thumbDimensions = [thumbWidth, thumbHeight] as [number, number];

  const renderMedia = (mediaListItem: MediaViewerMediaObject): JSX.Element => {
    const currentSlideIndex = slider?.current?.getCurrentIndex() || 0;
    const slideIndex = mediaList.indexOf(mediaListItem);
    const { media } = mediaListItem;
    const { guid, mediaType, mediaStatus, url, fileKey, mediaStorageStatus, created } = media;

    const uploadedDate = formatIntlDate({
      date: created,
      format: legendFormat,
    });

    switch (mediaType.code) {
      case MEDIA_TYPE.DOCUMENT:
        return (
          <CloudinaryPhoto
            mediaStorageStatus={mediaStorageStatus}
            guid={guid}
            fileKey={fileKey}
            customSize={mainDimensions}
            url={url}
            withDownloadLink={withDownloadLink}
            crop="fit"
          />
        );
      case MEDIA_TYPE.PHOTO:
        return (
          <CloudinaryPhoto
            guid={guid}
            customSize={mainDimensions}
            fileKey={fileKey}
            url={url}
            withDownloadLink={withDownloadLink && !(hideLockedBanner && media.isLocked)}
            crop="fit"
            transformation={media.transformation}
            mediaStorageStatus={media.mediaStorageStatus}
            isPhotoLocked={!hideLockedBanner && media.isLocked}
            legend={() =>
              withLegend ? (
                <StyledLegend>
                  {t('common:cloudinaryUploader.uploadedDate', {
                    uploadedDate,
                  })}
                </StyledLegend>
              ) : null
            }
          />
        );
      case MEDIA_TYPE.AUDIO:
      case MEDIA_TYPE.VIDEO:
        return (
          <CloudinaryVideoAudio
            url={url}
            guid={guid}
            preload="metadata"
            mediaType={mediaType.code}
            mediaStatus={mediaStatus.code}
            mediaStorageStatus={media.mediaStorageStatus?.code}
            customSize={mainDimensions}
            thumbnailUrl={media.thumbnailUrl}
            fileKey={media.fileKey}
            withDownloadLink={withDownloadLink}
            isVisible={currentSlideIndex === slideIndex}
          />
        );
      default:
        return null;
    }
  };

  const renderMediaWithWrapper = preventDownload
    ? (mediaListItem): JSX.Element => (
        <PreventDownloadContainer preventContextMenu>{renderMedia(mediaListItem)}</PreventDownloadContainer>
      )
    : renderMedia;

  const renderMediaThumbnail = ({ media }: MediaViewerMediaObject): JSX.Element => {
    const { guid, name, mediaType, mediaStatus, fileKey, mediaStorageStatus, thumbnailUrl, thumbnail } = media;

    switch (mediaType.code) {
      case MEDIA_TYPE.DOCUMENT:
        return <img src={DOCUMENT_THUMBNAIL} alt={name} height={thumbHeight} />;
      case MEDIA_TYPE.PHOTO:
        return (
          <CloudinaryPhoto
            mediaStorageStatus={media.mediaStorageStatus}
            transformation={media.transformation}
            fileKey={fileKey}
            autoRotateDimensions={false}
            guid={guid}
            customSize={thumbDimensions}
            crop="fit"
          />
        );
      case MEDIA_TYPE.AUDIO:
        return <img src={AUDIO_THUMBNAIL} alt={name} height={thumbHeight} />;
      case MEDIA_TYPE.VIDEO:
        return (
          <CloudinaryVideoAudio
            guid={guid}
            isThumbnail
            mediaType={mediaType.code}
            mediaStatus={mediaStatus.code}
            fileKey={fileKey}
            customSize={thumbDimensions}
            mediaStorageStatus={mediaStorageStatus?.code}
            thumbnailUrl={thumbnailUrl}
            thumbnail={thumbnail}
          />
        );
      default:
        return null;
    }
  };

  const renderMediaThumbnailWithWrapper = preventDownload
    ? (thumb): JSX.Element => (
        <PreventDownloadContainer preventContextMenu>{renderMediaThumbnail(thumb)}</PreventDownloadContainer>
      )
    : renderMediaThumbnail;

  const renderLeftNav = (onClick, disabled: boolean): JSX.Element => (
    <StyledPrevButton onClick={onClick} disabled={disabled} />
  );
  const renderRightNav = (onClick, disabled: boolean): JSX.Element => (
    <StyledNextButton onClick={onClick} disabled={disabled} />
  );

  return (
    <StyledModal
      data-testid="cloudinary-media-viewer"
      zIndex={zIndex}
      $isSmall={isSmall}
      isOpen
      toggle={handleCloseModal}
    >
      <ModalHeader
        toggle={handleCloseModal}
        close={
          <StyledCloseButton
            data-qa-id={`${SCREEN_NAMES.MEDIA_VIEWER}-close-button`}
            aria-label={t('common:button.close')}
            className="close"
            onClick={handleCloseModal}
            onKeyDown={handleCloseModal}
          >
            <Icon name="close" themeColor="inverted" />
          </StyledCloseButton>
        }
      >
        <span data-qa-id={`${SCREEN_NAMES.MEDIA_VIEWER}-title`}>{stageName}</span>
      </ModalHeader>
      <StyledModalBody data-qa-id={`${SCREEN_NAMES.MEDIA_VIEWER}-modal-body`}>
        <ImageGallery
          ref={slider}
          items={mediaList}
          indexSeparator={t('common:label.of')}
          showFullscreenButton={false}
          showIndex
          showPlayButton={false}
          slideDuration={250}
          startIndex={startIndex}
          renderItem={renderMediaWithWrapper}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
          renderThumbInner={renderMediaThumbnailWithWrapper}
        />
        {widthData.moreMd && mediaList.length > 1 && (
          <ThumbNav>
            <ThumbPrevButton onClick={(): void => slider.current.slideLeft()} />
            <ThumbNextButton onClick={(): void => slider.current.slideRight()} />
          </ThumbNav>
        )}
      </StyledModalBody>
    </StyledModal>
  );
};

export default withTheme(CloudinaryMediaViewer);
